/** TODO temporary. will be refactored */

import { ActionsObservable, ofType, StateObservable } from 'redux-observable';
import { forkJoin, of } from 'rxjs';
import {
    catchError,
    concatMap,
    map,
    mergeMap,
    switchMap,
} from 'rxjs/operators';

import { Dependencies } from '../store';
import { pluck } from '../store.decorator';
import { PayloadAction } from '../store.model';

import { Column, Row, Sort } from './experimental.grid';
import {
    ActionType,
    RootInitAction,
    rootInitResult,
    RootListLookupsAction,
    rootListLookupsResult,
    RootSearchAction,
    rootSearchResult,
    RootSelectUserAction,
    rootSelectUserResult,
    RootToggleFavoriteAction,
    rootToggleFavoriteResult,
} from './search-user.action';
import { CriteriaFormValue } from './search-user.form';
import {
    listLookups,
    listPeopleOfInterest,
    listTeam,
    markFavorites,
    retrieveManager,
    retrieveUser,
    search,
    togglePersonOfInterest,
} from './search-user.function';
import { UserSearchResult } from './search-user.model';
import { RootState } from './search-user.state';
import { User } from './user.model';

export interface ChainGroup<TPayload> {
    root: TPayload;

    teamColumns?: Array<Column<string>>;
    teamRows?: Array<Row<any, UserSearchResult>>;
    teamSortDefs?: Sort[];

    favoritesColumns?: Array<Column<string>>;
    favoritesRows?: Array<Row<any, UserSearchResult>>;
    favoritesSortDefs?: Sort[];

    // criteria?: UserSearchCriteria;
    criteria?: CriteriaFormValue;
    searchFailed?: boolean;
    searchExceedsMaxCount?: boolean;

    searchColumns?: Array<Column<string>>;
    searchRows?: Array<Row<any, UserSearchResult>>;
    searchSortDefs?: Sort[];

    userProfile?: User;
    user?: UserSearchResult;
    manager?: UserSearchResult;
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const rootInit = (
    action: ActionsObservable<PayloadAction<ActionType, RootInitAction>>,
    state: StateObservable<any>,
    deps: Dependencies,
) =>
    action.pipe(
        ofType(ActionType.SEARCH_USER__ROOT__INIT),
        switchMap((act) => {
            const grp: ChainGroup<RootInitAction> = {
                root: act.payload,
            };

            return of(null).pipe(
                concatMap(() => {
                    return forkJoin([
                        retrieveUser(deps, grp, act.payload.selectedUserId),
                        retrieveManager(deps, grp, act.payload.selectedUserId),
                        listTeam(deps, grp, act.payload.selectedUserId),
                        listPeopleOfInterest(deps, grp, act.payload.scope),
                    ]);
                }),
                markFavorites(grp), // TODO move to reducer
                map(() =>
                    rootInitResult(
                        {
                            ...act.payload,
                            teamColumns: grp.teamColumns,
                            teamRows: grp.teamRows,
                            favoritesColumns: grp.favoritesColumns,
                            favoritesRows: grp.favoritesRows,
                            selectedUserProfile: grp.userProfile,
                            selectedUser: grp.user,
                            selectedUserManager: grp.manager,
                        },
                        act.storePath,
                    ),
                ),
                catchError((err) => {
                    console.log(err);
                    return of(
                        rootInitResult({ ...act.payload }, act.storePath),
                    );
                }),
            );
        }),
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const rootListLookups = (
    action: ActionsObservable<PayloadAction<ActionType, RootListLookupsAction>>,
    state: StateObservable<any>,
    deps: Dependencies,
) =>
    action.pipe(
        ofType(ActionType.SEARCH_USER__ROOT__LIST_LOOKUPS),
        switchMap((act) => {
            return of(null).pipe(
                listLookups(deps),
                map((ret) =>
                    rootListLookupsResult(
                        {
                            ...act.payload,
                            lobs: ret.lobs,
                            regions: ret.regions,
                            areas: ret.areas,
                            sites: ret.sites,
                            superGroups: ret.superGroups,
                            roles: ret.roles,
                        },
                        act.storePath,
                    ),
                ),
                catchError((err) => {
                    console.log(err);
                    return of(
                        rootListLookupsResult(
                            { ...act.payload },
                            act.storePath,
                        ),
                    );
                }),
            );
        }),
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const rootSearch = (
    action: ActionsObservable<PayloadAction<ActionType, RootSearchAction>>,
    state: StateObservable<any>,
    deps: Dependencies,
) =>
    action.pipe(
        ofType(ActionType.SEARCH_USER__ROOT__SEARCH),
        switchMap((act) => {
            const rootState = pluck<RootState>(state.value, act.storePath);
            const grp: ChainGroup<RootSearchAction> = {
                root: act.payload,
                criteria: rootState.criteria,
                searchColumns: rootState.searchColumns,
            };

            return of(null).pipe(
                search(deps, grp, rootState.scope),
                map(() =>
                    rootSearchResult(
                        {
                            searchFailed: grp.searchFailed,
                            searchExceedsMaxCount: grp.searchExceedsMaxCount,
                            columns: grp.searchColumns,
                            rows: grp.searchRows,
                        },
                        act.storePath,
                    ),
                ),
                catchError((err) => {
                    console.log(err);
                    return of(
                        rootSearchResult({ ...act.payload }, act.storePath),
                    );
                }),
            );
        }),
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const rootSelectUser = (
    action: ActionsObservable<PayloadAction<ActionType, RootSelectUserAction>>,
    state: StateObservable<any>,
    deps: Dependencies,
) =>
    action.pipe(
        ofType(ActionType.SEARCH_USER__ROOT__SELECT_USER),
        switchMap((act) => {
            const rootState = pluck<RootState>(state.value, act.storePath);
            const grp: ChainGroup<RootSelectUserAction> = {
                root: act.payload,
                favoritesRows: rootState.favoritesRows,
            };

            return of(null).pipe(
                concatMap(() => {
                    return forkJoin([
                        retrieveUser(deps, grp, act.payload.userId),
                        retrieveManager(deps, grp, act.payload.userId),
                        listTeam(deps, grp, act.payload.userId),
                        listPeopleOfInterest(deps, grp, rootState.scope),
                    ]);
                }),
                markFavorites(grp),
                map(() =>
                    rootSelectUserResult(
                        {
                            userId: grp.root.userId,
                            userProfile: grp.userProfile,
                            user: grp.user,
                            manager: grp.manager,
                            teamRows: grp.teamRows,
                        },
                        act.storePath,
                    ),
                ),
                catchError((err) => {
                    console.log(err);
                    return of(
                        rootSelectUserResult({ ...act.payload }, act.storePath),
                    );
                }),
            );
        }),
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const rootToggleFavorite = (
    action: ActionsObservable<
        PayloadAction<ActionType, RootToggleFavoriteAction>
    >,
    state: StateObservable<any>,
    deps: Dependencies,
) =>
    action.pipe(
        ofType(ActionType.SEARCH_USER__ROOT__TOGGLE_FAVORITE),
        mergeMap((act) => {
            const rootState = pluck<RootState>(state.value, act.storePath);
            const grp: ChainGroup<RootToggleFavoriteAction> = {
                root: act.payload,
                teamRows: rootState.teamRows,
                user: rootState.selectedUser,
            };

            return of(null).pipe(
                togglePersonOfInterest(deps, grp, act.payload.user),
                concatMap(() =>
                    listPeopleOfInterest(deps, grp, rootState.scope),
                ),
                markFavorites(grp),
                map(() =>
                    rootToggleFavoriteResult(
                        {
                            user: grp.root.user,
                            refId: grp.root.refId,
                            favoritesRows: grp.favoritesRows,
                            selectedUser: grp.user,
                        },
                        act.storePath,
                    ),
                ),
                catchError((err) => {
                    console.log(err);
                    return of(
                        rootToggleFavoriteResult(
                            { ...act.payload },
                            act.storePath,
                        ),
                    );
                }),
            );
        }),
    );

export default [
    rootInit,
    rootListLookups,
    rootSearch,
    rootSelectUser,
    rootToggleFavorite,
];
